import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  IconButton,
  Typography,
  Box,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { useFormik } from "formik";
import * as Yup from "yup";
import { usePlaygroundContext } from "../Playground";
import { bookDemo } from "../../api/agent"; // Import the bookDemo function
import MasterDataAutoComplete from "./MasterDataAutoComplete";
interface ContactFormProps {
  open: boolean;
  onClose: () => void;
}

const ContactForm: React.FC<ContactFormProps> = ({ open, onClose }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">("success");

  const playgroundContext = usePlaygroundContext();
  
  if (!playgroundContext) {
    throw new Error("PlaygroundContext is not available");
  }

  const { playground, setPlayground } = playgroundContext;

  const firstName = playground.fullName;
  const busineesName = playground.companyName;
  const busineesEmail = playground.companyEmail;
  const industry = playground?.jobDescription?.messageList[0]?.industry.name;
  const phone = playground.phoneNumber;

  const formik = useFormik({
    initialValues: {
      firstName: "",
      businessName: "",
      industry: "",
      businessEmail: "",
      mobileNumber: "",
      message: "",
    },
    validationSchema: Yup.object({
      firstName: Yup.string().required("Required"),
      businessName: Yup.string().required("Required"),
      industry: Yup.string().required("Required"),
      businessEmail: Yup.string().email("Invalid email").required("Required"),
      mobileNumber: Yup.string().required("Required"),
    }),
    onSubmit: (values) => {
      setIsSubmitting(true);
      // Prepare the payload for the API
      const payload = {
        firstname: values.firstName,
        lastname: "", // Since there's no lastname field in your form, sending empty
        business_name: values.businessName,
        business_email: values.businessEmail,
        industry: values.industry,
        phone: values.mobileNumber,
        user_comments: values.message
      };

      // Make the API call
      bookDemo(payload)
        .then(() => {
          setSnackbarMessage("Thank you for requesting a demo. Our team will reach out to you shortly to schedule a session.");
          setSnackbarSeverity("success");
          setSnackbarOpen(true);
          onClose();
        })
        .catch(() => {
          setSnackbarMessage("Error submitting demo request. Please try again.");
          setSnackbarSeverity("error");
          setSnackbarOpen(true);
        })
        .finally(() => {
          setIsSubmitting(false);
        });
    },
  });

  // Update form values when props change
  useEffect(() => {
    formik.setValues({
      firstName: firstName || "",
      businessName: busineesName || "",
      industry: industry || "",
      businessEmail: busineesEmail || "",
      mobileNumber: phone || "",
      message: "", // Keep message empty
    });
  }, [firstName, busineesName, busineesEmail, industry, phone, open]);

  // Common TextField styles
  const textFieldStyles = {
    "& .MuiOutlinedInput-root": {
      height: "42px",
      backgroundColor: "#F7F7F7",
      borderRadius: "4px",
      "& fieldset": {
        borderColor: "#C6C6C6", // Ensure a single border
      },
      "&:hover fieldset": {
        borderColor: "#C6C6C6", // Slightly darker on hover
      },
      "&.Mui-focused fieldset": {
        borderColor: "#C6C6C6", // Correct border color on focus
      },
      "& input": {
        color: "#495057",
      },
      "& .MuiInputLabel-root": {
        color: "#b0b7c3",
      },
      "& .MuiInputLabel-root.Mui-focused": {
        color: "#ccc",
      },
      "& .MuiOutlinedInput-input": {
        padding: "9.5px 14px",
        "&::placeholder": {
          color: "#303030",
          opacity: 1,
        },
      },
    },
  };
  
  const messageField = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#F7F7F7",
      borderRadius: "4px", // Keep consistent rounding
      "& fieldset": {
        borderColor: "#C6C6C6", // Default border color
      },
      "&:hover fieldset": {
        borderColor: "#C6C6C6", // Slightly darker on hover
      },
      "&.Mui-focused fieldset": {
        borderColor: "#C6C6C6", // Correct border color on focus
      },
      "& input, & textarea": {
        color: "#495057",
      },
    },
    "& .MuiInputLabel-root": {
      color: "#b0b7c3",
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: "#424965",
    },
    "& .MuiOutlinedInput-input": {
      maxHeight: "70px", // Ensure height works correctly for multiline
      "&::placeholder": {
        color: "#303030",
        opacity: 1,
      },
    },
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
        <DialogTitle sx={{ textAlign: "center", position: "relative" }}>
          <Typography
            sx={{ fontWeight: "900", fontSize: "22px", marginTop: "10px" }}
          >
            Share your information to book a personalized walkthrough
          </Typography>

          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <form onSubmit={formik.handleSubmit}>
            <Grid container spacing={2}>
              {/* First Name & Last Name */}
              <Grid item xs={12} sm={6}>
                <TextField
                  name="fullName"
                  placeholder="Full Name*"
                  fullWidth
                  variant="outlined"
                  value={formik.values.firstName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.firstName && Boolean(formik.errors.firstName)
                  }
                helperText={formik.touched.firstName && formik.errors.firstName}
                  sx={textFieldStyles}
                  disabled={Boolean(formik.values.firstName)} // Disable if populated
                />
              </Grid>
              {/* Business Name & Industry */}
              <Grid item xs={12} sm={6}>
                <TextField
                  name="businessName"
                  placeholder="Business Name*"
                  fullWidth
                  variant="outlined"
                  value={formik.values.businessName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.businessName &&
                    Boolean(formik.errors.businessName)
                  }
                  helperText={
                    formik.touched.businessName && formik.errors.businessName
                  }
                  sx={textFieldStyles}
                  disabled={Boolean(formik.values.businessName)} // Disable if populated
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <MasterDataAutoComplete
                  label="Industry"
                  type="industry"
                  selectedValue={formik.values.industry}
                  setSelectedValue={(value) =>
                    formik.setFieldValue("industry", value || "")
                  }
                  onBlur={formik.handleBlur("industry")}
                  multiple={false}
                  inputTextStyle={textFieldStyles}
                />
                {formik.touched.industry && formik.errors.industry && (
                  <Typography color="error" variant="caption">
                    {formik.errors.industry}
                  </Typography>
                )}
              </Grid>

              {/* Email & Phone Number */}
              <Grid item xs={12} sm={6}>
                <TextField
                  name="businessEmail"
                  placeholder="Business Email*"
                  fullWidth
                  variant="outlined"
                  value={formik.values.businessEmail}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.businessEmail &&
                    Boolean(formik.errors.businessEmail)
                  }
                  helperText={
                    formik.touched.businessEmail && formik.errors.businessEmail
                  }
                  sx={textFieldStyles}
                  disabled={Boolean(formik.values.businessEmail)} // Disable if populated
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box
                  sx={{
                    "& .react-tel-inputContactUs input::placeholder": {
                      color: "#303030",
                      opacity: 1,
                    },
                    "& .react-tel-inputContactUs .flag-dropdown": {
                      backgroundColor: "#F7F7F7 !important",
                      border: "1px solid #C6C6C6 !important",
                    },
                    "& .react-tel-inputContactUs .flag-dropdown.open": {
                      backgroundColor: "grey !important",
                    },
                  }}
                >
                  <PhoneInput
                    placeholder="Phone"
                    containerClass="react-tel-inputContactUs"
                    inputProps={{
                      required: true,
                      "data-testid": "input-phone",
                      disabled: Boolean(formik.values.mobileNumber), // Disable if populated
                    }}
                    inputStyle={{
                      height: "42px",
                      width: "100%",
                      backgroundColor: "#F7F7F7",
                      border: "1px solid #C6C6C6",
                      borderRadius: "4px",
                      padding: "10.5px 14px",
                      paddingLeft: "50px",
                      color: "#495057", // Updated text color
                      fontFamily: "Lato",
                      fontSize: "14px",
                      lineHeight: "18px",
                      outline: "none",
                    }}
                    buttonStyle={{
                      backgroundColor: "#F7F7F7 !important",
                      border: "1px solid #C6C6C6 !important",
                      borderRadius: "4px 0 0 4px",
                    }}
                    dropdownStyle={{
                      backgroundColor: "#F7F7F7 !important",
                      color: "grey",
                    }}
                    country={"us"}
                    preserveOrder={["preferredCountries"]}
                    value={formik.values.mobileNumber} // Auto-populate value
                    onChange={(value) =>
                      formik.setFieldValue("mobileNumber", value)
                    } // Update Formik state
                  />
                </Box>
                {formik.touched.mobileNumber && formik.errors.mobileNumber && (
                  <Typography color="error" variant="caption">
                    {formik.errors.mobileNumber}
                  </Typography>
                )}
              </Grid>

              {/* Message Box with Increased Height */}
              <Grid item xs={12}>
                <TextField
                  name="message"
                  placeholder="Message"
                  fullWidth
                  multiline
                  rows={6} // Increased height
                  variant="outlined"
                  value={formik.values.message}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  sx={messageField}
                />
              </Grid>
            </Grid>

            <DialogActions sx={{ justifyContent: "center", padding: 2 }}>
              <Button
                type="submit"
                variant="contained"
                disabled={isSubmitting}
                sx={{
                  marginBottom: "10px",
                  backgroundColor: "#007BFF",
                  borderRadius: "30px",
                  px: 3,
                  py: 1.5,
                  fontSize: "14px",
                  fontWeight: "700",
                  color: "white",
                  textTransform: "none",
                  "&:hover": { backgroundColor: "#0056b3" },
                }}
              >
                {isSubmitting ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Submit"
                )}
              </Button>
            </DialogActions>
          </form>
        </DialogContent>
      </Dialog>

      {/* Snackbar Component */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }} // Positioning
      >
        <Alert
          severity={snackbarSeverity}
          onClose={() => setSnackbarOpen(false)}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ContactForm;